import React, { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import TurnstileWidget from "../components/TurnstileWidget";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileWrapRef = useRef(null);
  const onTurnstileToken = useCallback((t) => setTurnstileToken(t), []);
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !turnstileToken) { toast.error("Please complete the human verification"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        turnstile_token: turnstileToken || undefined,
      });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, "Request failed"));
      // Reset Turnstile on any failure — each token is single-use
      try { turnstileWrapRef.current?.querySelector('[data-testid="turnstile-widget"]')?.__resetTurnstile?.(); } catch { /* widget may already be gone */ }
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">[03] Recover access</div>
      <h1 className="font-display font-bold text-5xl mb-8">Forgot password</h1>

      {!sent ? (
        <form onSubmit={submit} className="cal-card p-8 space-y-5" data-testid="forgot-form">
          <p className="text-[#666] text-sm">
            Enter your account email. If it's registered, we'll send a one-time reset link valid for 1 hour.
          </p>
          <div>
            <label className="cal-label">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="cal-input" data-testid="forgot-email" autoComplete="email" />
          </div>
          {TURNSTILE_SITE_KEY && (
            <div ref={turnstileWrapRef} data-testid="forgot-turnstile">
              <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={onTurnstileToken} />
            </div>
          )}
          <button disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)} className="cal-btn w-full" data-testid="forgot-submit">
            {loading ? "Sending…" : "Send reset link →"}
          </button>
        </form>
      ) : (
        <div className="cal-card p-8 space-y-4" data-testid="forgot-result">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C]">[ CHECK YOUR INBOX ]</div>
          <h2 className="font-display font-bold text-2xl">Request received</h2>
          <p className="text-[#666] text-sm leading-relaxed">
            If an account exists for <strong className="text-[#111]">{email}</strong>, a one-time reset link has been emailed to that address. It expires in 1 hour.
          </p>
          <p className="text-[#666] text-sm">
            If you don't see it within a few minutes, check your spam or promotions folder.
          </p>
          <div className="pt-2">
            <Link to="/login" className="cal-btn cal-btn-ghost" data-testid="back-login">← Back to sign in</Link>
          </div>
        </div>
      )}

      {!sent && (
        <p className="mt-6 text-sm text-[#666]">
          Remembered it? <Link to="/login" className="underline hover:text-[#FF4F00]" data-testid="back-login-link">Sign in</Link>
        </p>
      )}
    </div>
  );
}
